import { useMemo } from 'react';
import type { Selection } from '@heroui/react';
import { useGetCoursesStructureQuery } from '@/entities/Courses';
import type { CourseType } from '@/entities/Dashboard';
import { CustomSelect } from '@/shared/ui/Fields';
import styles from '../../dashboard/Dashboard.module.scss';

export interface DashboardFilterValue {
  type?: CourseType;
  courseId?: number;
  intakeId?: number;
  termId?: number;
  groupId?: number;
}

interface DashboardFiltersProps {
  value: DashboardFilterValue;
  onChange: (value: DashboardFilterValue) => void;
  showTerm?: boolean;
  showGroup?: boolean;
  twoRow?: boolean;
}

const TYPE_OPTIONS: { key: string; label: string }[] = [
  { key: 'masters-degree', label: "Master's Degree" },
  { key: 'pre-masters-degree', label: "Pre-Master's Degree" },
  { key: 'bachelors-degree', label: "Bachelor's Degree" },
  { key: 'short', label: 'Short course' },
];

const firstKey = (keys: Selection): string | undefined => {
  if (keys === 'all') return undefined;
  const first = Array.from(keys)[0];
  return first === undefined ? undefined : String(first);
};

export const DashboardFilters = ({
  value,
  onChange,
  showTerm = false,
  showGroup = true,
  twoRow = false,
}: DashboardFiltersProps) => {
  const { data } = useGetCoursesStructureQuery();
  const courses = useMemo(() => data?.data ?? [], [data]);

  const filteredCourses = useMemo(
    () => (value.type ? courses.filter((c) => c.type === value.type) : courses),
    [courses, value.type],
  );

  const selectedCourse = useMemo(
    () => courses.find((c) => c.id === value.courseId),
    [courses, value.courseId],
  );

  const intakes = selectedCourse?.intakes ?? [];
  const selectedIntake = intakes.find((i) => i.id === value.intakeId);
  const terms = selectedIntake?.terms ?? [];
  const selectedTerm = terms.find((t) => t.id === value.termId);
  const groups = selectedTerm?.groups ?? [];

  const courseItems = filteredCourses.map((c) => ({ key: String(c.id), label: c.title }));
  const intakeItems = intakes.map((i) => ({ key: String(i.id), label: i.title }));
  const termItems = terms.map((t) => ({ key: String(t.id), label: `Term ${t.number}` }));
  const groupItems = groups.map((g) => ({ key: String(g.id), label: g.title || `G${g.number}` }));

  const handleType = (keys: Selection) => {
    const key = firstKey(keys);
    onChange({ type: key ? (key as CourseType) : undefined });
  };

  const handleCourse = (keys: Selection) => {
    const key = firstKey(keys);
    onChange({ ...value, courseId: key ? Number(key) : undefined, intakeId: undefined, termId: undefined, groupId: undefined });
  };

  const handleIntake = (keys: Selection) => {
    const key = firstKey(keys);
    onChange({ ...value, intakeId: key ? Number(key) : undefined, termId: undefined, groupId: undefined });
  };

  const handleTerm = (keys: Selection) => {
    const key = firstKey(keys);
    onChange({ ...value, termId: key ? Number(key) : undefined, groupId: undefined });
  };

  const handleGroup = (keys: Selection) => {
    const key = firstKey(keys);
    onChange({ ...value, groupId: key ? Number(key) : undefined });
  };

  return (
    <div className={twoRow ? styles.filtersGridTwo : styles.filtersGrid}>
      <CustomSelect
        style="black"
        label="Type"
        items={TYPE_OPTIONS}
        selectedKeys={value.type ? new Set([value.type]) : new Set()}
        onSelectionChange={handleType}
        aria-label="Type"
      />
      <CustomSelect
        style="black"
        label="Course"
        items={courseItems}
        selectedKeys={value.courseId ? new Set([String(value.courseId)]) : new Set()}
        onSelectionChange={handleCourse}
        aria-label="Course"
      />
      <CustomSelect
        style="black"
        label="Intake"
        items={intakeItems}
        selectedKeys={value.intakeId ? new Set([String(value.intakeId)]) : new Set()}
        onSelectionChange={handleIntake}
        aria-label="Intake"
      />
      {showTerm ? (
        <CustomSelect
          style="black"
          label="Term"
          items={termItems}
          selectedKeys={value.termId ? new Set([String(value.termId)]) : new Set()}
          onSelectionChange={handleTerm}
          aria-label="Term"
        />
      ) : null}
      {showGroup ? (
        <CustomSelect
          style="black"
          label="Group"
          items={groupItems}
          selectedKeys={value.groupId ? new Set([String(value.groupId)]) : new Set()}
          onSelectionChange={handleGroup}
          aria-label="Group"
        />
      ) : null}
    </div>
  );
};
